import { type Result, ResultFactory } from "@/types/generic/Result"

export type PostChangeLocaleBody = {
    /** ISO 3166-1 alpha-2 country code (e.g. "SE", "DE"). */
    countryCode: string
    /** Centra language code (e.g. "en", "de"). Derived from the target locale if absent. */
    languageCode?: string
    /** The target locale path including leading slash (e.g. "/en-se"). Stored as preference. */
    preferredApp: string
}

/**
 * POSTs to `/api/centra/change-language-or-shipping`.
 *
 * The route handles both branches:
 * - **Active basket** (token present in cookie): calls Centra `changeCountry` and
 *   writes back the active-shape `basket_session` cookie.
 * - **No basket / preference only**: writes a preference-shape cookie without any
 *   Centra HTTP call.
 *
 * Callers never need to inspect the cookie themselves — they just POST and navigate.
 */
export const postChangeLanguageOrShipping = async (
    body: PostChangeLocaleBody
): Promise<Result<object>> => {
    try {
        const res = await fetch("/api/centra/change-language-or-shipping", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            credentials: "include"
        })

        if (!res.ok) {
            const json: { error?: string } = await res.json().catch(() => ({}))
            return ResultFactory.failure(
                new Error(json?.error ?? `Request failed with status ${res.status}`)
            )
        }

        return ResultFactory.success({})
    } catch (error) {
        return ResultFactory.failure(
            error instanceof Error ? error : new Error(String(error))
        )
    }
}
